import type { AlertRule, ProcessStatus, ProcessSummary } from '@pm2-monitor/shared';
import { formatSeconds, toMiB } from './format';

/**
 * Status colours. pm2 has six lifecycle states but only three readings matter
 * visually: running, failed, and everything in between.
 */
export function statusColors(status: ProcessStatus): {
  dot: string;
  ring: string;
} {
  if (status === 'online') return { dot: 'var(--pos)', ring: 'var(--pos-tint)' };
  if (status === 'errored') return { dot: 'var(--neg)', ring: 'var(--neg-tint)' };
  return { dot: 'var(--faint)', ring: 'transparent' };
}

export function statusPill(status: ProcessStatus): { bg: string; fg: string } {
  if (status === 'online') return { bg: 'var(--pos-tint)', fg: 'var(--pos)' };
  if (status === 'errored') return { bg: 'var(--neg-tint)', fg: 'var(--neg)' };
  return { bg: 'var(--page)', fg: 'var(--muted)' };
}

/**
 * pm2's restart counter is only alarming in context: a long-lived process that
 * has restarted twice in two months is healthy. The design's threshold of ten
 * is kept, and `unstableRestarts` — pm2's own count of restarts inside its
 * minimum-uptime window — always reads as a problem.
 */
export function restartTone(process: ProcessSummary): {
  bg: string;
  fg: string;
} {
  if (process.unstableRestarts > 0 || process.restarts > 9) {
    return { bg: 'var(--neg-tint)', fg: 'var(--neg)' };
  }
  return { bg: 'var(--page)', fg: 'var(--muted)' };
}

/**
 * Human phrasing for an alert rule, derived from its live configuration rather
 * than hard-coded, so editing a threshold on the phone is reflected here.
 */
export function describeRule(rule: AlertRule): {
  label: string;
  detail: string;
} {
  const scope = rule.processNames.length > 0 ? ` · ${rule.processNames.join(', ')}` : '';
  const window =
    rule.durationSeconds > 0 ? ` for ${formatSeconds(rule.durationSeconds)}` : '';

  switch (rule.kind) {
    case 'crash':
      return {
        label: 'A process crashes',
        detail: `status leaves online${scope}`,
      };
    case 'restart_loop':
      return {
        label: 'A process restart-loops',
        detail: `${rule.threshold ?? 3} restarts in ${formatSeconds(rule.durationSeconds)}${scope}`,
      };
    case 'cpu_high':
      return {
        label: 'CPU stays high',
        detail: `above ${rule.threshold ?? 80}%${window}${scope}`,
      };
    case 'memory_high':
      return {
        label: 'Memory stays high',
        detail: `above ${toMiB(rule.threshold ?? 0).toFixed(0)} MiB${window}${scope}`,
      };
    case 'daemon_unreachable':
      return {
        label: 'pm2 daemon unreachable',
        detail: 'bridge lost the daemon',
      };
  }
}

export function severityPill(severity: 'info' | 'warning' | 'critical'): {
  bg: string;
  fg: string;
  label: string;
} {
  if (severity === 'critical') {
    return { bg: 'var(--neg-tint)', fg: 'var(--neg)', label: 'Critical' };
  }
  if (severity === 'warning') {
    return { bg: 'var(--page)', fg: 'var(--muted)', label: 'Warning' };
  }
  return { bg: 'var(--pos-tint)', fg: 'var(--pos)', label: 'Info' };
}

/**
 * Trailing slice of a sample ring for the selected window. The bridge emits one
 * frame per `statusIntervalMs`, so a window is simply a sample count — and when
 * the tab has been open for less than the window, whatever exists is returned
 * rather than padding the chart with zeroes it never measured.
 */
export function windowSlice<T>(
  samples: readonly T[],
  rangeSeconds: number,
  intervalMs: number,
): T[] {
  const wanted = Math.max(2, Math.round((rangeSeconds * 1000) / Math.max(1, intervalMs)));
  return samples.slice(Math.max(0, samples.length - wanted));
}

/** `−60s … now` ticks under a chart, spaced to the window actually drawn. */
export function timeAxis(coveredSeconds: number): string[] {
  const step = coveredSeconds / 4;
  return [4, 3, 2, 1, 0].map((n) => {
    const back = Math.round(step * n);
    if (back === 0) return 'now';
    if (back < 60) return `−${back}s`;
    if (back < 3600) return `−${Math.round(back / 60)}m`;
    return `−${(back / 3600).toFixed(back % 3600 === 0 ? 0 : 1)}h`;
  });
}

export const RANGE_LABELS: Record<number, string> = {
  60: '60s',
  900: '15m',
  3600: '1h',
};
